import {
  type createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  notFound,
} from "@tanstack/react-router";
import { Layout } from "./components/Layout";
import { PageMeta } from "./components/PageMeta";
import { projects } from "./data/site";
import { useI18n } from "./i18n";
import { AboutPage } from "./pages/AboutPage";
import { AdminPage } from "./pages/AdminPage";
import { CapabilitiesPage } from "./pages/CapabilitiesPage";
import { ContactPage } from "./pages/ContactPage";
import { HomePage } from "./pages/HomePage";
import { LogoPage } from "./pages/LogoPage";
import { ProductsPage } from "./pages/ProductsPage";
import { WorkDetailPage } from "./pages/WorkDetailPage";
import { WorkPage } from "./pages/WorkPage";

function NotFound() {
  const { l } = useI18n();
  return (
    <section className="not-found">
      <PageMeta title={l("页面未找到", "Page not found")} />
      <span>404 / SIGNAL LOST</span>
      <h1>{l("这里没有找到系统。", "We couldn't find that page.")}</h1>
      <p>
        {l(
          "页面可能已移动，或者这个路径尚未接入。",
          "It may have moved, or this route is not connected yet.",
        )}
      </p>
      <Link className="button" to="/">
        {l("返回首页", "Back home")}
      </Link>
    </section>
  );
}

const rootRoute = createRootRoute({
  component: Layout,
  notFoundComponent: NotFound,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});
const capabilitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/capabilities",
  component: CapabilitiesPage,
});
const workRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work",
  component: WorkPage,
});
const workDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/$projectId",
  beforeLoad: ({ params }) => {
    if (!projects.some((project) => project.slug === params.projectId)) {
      throw notFound();
    }
  },
  component: WorkDetailPage,
});
const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products",
  component: ProductsPage,
});
const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/about",
  component: AboutPage,
});
const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contact",
  component: ContactPage,
});
const logoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/logo",
  component: LogoPage,
});
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  capabilitiesRoute,
  workRoute,
  workDetailRoute,
  productsRoute,
  aboutRoute,
  contactRoute,
  logoRoute,
  adminRoute,
]);

export function createAppRouter(
  history?: ReturnType<typeof createMemoryHistory>,
) {
  return createRouter({ routeTree, history, scrollRestoration: true });
}

export const router = createAppRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
